import { differenceInMinutes, isBefore, isAfter, startOfDay, subDays, set, getHours, addMinutes, isEqual, isWithinInterval, isSameDay, isValid, min, max } from "date-fns";
import { SLOT_TIME } from "../../appointment-schedule.constants.js";
import { sortByDuration, sortByStartTime } from "../../helpers/groupBy.js";
import { splitSlotsToIntervals } from "./helpers/splitSlotsToIntervals.js";
import "../../../../../utils/time/time.js";
import "lodash";
import "../../appointment-schedule.types.js";
import "../../helpers/common.js";
import "react";
import "date-fns/locale";
import "fast-memoize";
const getSlotTopPosition = (start, startHour, duration) => {
  const startInMinutes = start.getMinutes() + start.getHours() * 60;
  const containerStartHourInMinutes = startHour.getMinutes() + startHour.getHours() * 60;
  const startTime = startInMinutes - containerStartHourInMinutes;
  return startTime / duration * 100;
};
const getSlotHeight = (start, end, duration) => {
  const appointmentDuration = differenceInMinutes(end, start);
  return 100 * appointmentDuration / duration;
};
const isOverlappingWithNonWorkingTime = (appointment, nonWorkingTimes) => nonWorkingTimes.some(({ start, end }) => {
  const appointmentEnd = appointment.end;
  const appointmentStart = appointment.start;
  return isBefore(start, appointmentEnd) && isAfter(end, appointmentStart);
});
const isPastAppointment = (time, end) => {
  const startOfYesterday = startOfDay(subDays(/* @__PURE__ */ new Date(), 1));
  return isAfter(time, end) && isBefore(end, startOfYesterday);
};
const getFreeSlots = (items, currentDay, dayStart, dayEnd, employeeReferenceId, workingTimes = false) => {
  const sortedItems = items.sort(sortByDuration).sort(sortByStartTime);
  const currentDayStart = set(currentDay, { hours: getHours(dayStart), minutes: 0, seconds: 0 });
  const currentDayEnd = set(currentDay, { hours: getHours(dayEnd), minutes: 0, seconds: 0 });
  const gapBetweenFirstAppointmentAndDayStart = differenceInMinutes(
    sortedItems.length ? sortedItems[0].start : currentDayEnd,
    currentDayStart
  );
  const initialSlot = gapBetweenFirstAppointmentAndDayStart >= SLOT_TIME ? {
    start: currentDayStart,
    end: addMinutes(currentDayStart, gapBetweenFirstAppointmentAndDayStart),
    employeeReferenceId
  } : null;
  const slots = sortedItems.reduce(
    (currentSlots, item, index, array) => {
      const nextAppointment = array[index + 1];
      const prevAppointment = array[index - 1];
      const usePreviousAppointmentStart = prevAppointment && isAfter(prevAppointment.end, item.end) && isBefore(prevAppointment.end, currentDayEnd);
      const start = usePreviousAppointmentStart ? prevAppointment.end : item.end;
      const isItemShorterThanPreviousAppointment = currentSlots.some(
        ({ end: currentSlotEnd, start: currentSlotStart }) => isBefore(currentSlotEnd, item.end) && (isAfter(currentSlotStart, item.start) || isEqual(currentSlotStart, item.start))
      );
      const isIntervalOverlapping = array.filter(({ start: itemStart, end: itemEnd }) => !isEqual(item.start, itemStart) && isBefore(itemStart, itemEnd)).some(({ end, start: itemStart }) => isWithinInterval(start, { start: itemStart, end }));
      if (isItemShorterThanPreviousAppointment || isIntervalOverlapping && !workingTimes)
        return currentSlots;
      if (!nextAppointment) {
        const gapUntilEndOfDay = differenceInMinutes(currentDayEnd, item.end);
        return gapUntilEndOfDay >= SLOT_TIME ? [
          ...currentSlots,
          {
            start,
            end: currentDayEnd,
            employeeReferenceId
          }
        ] : currentSlots;
      }
      const gapBetweenAppointments = differenceInMinutes(nextAppointment.start, item.end);
      if (gapBetweenAppointments >= SLOT_TIME) {
        return [
          ...currentSlots,
          {
            start,
            end: nextAppointment.start,
            employeeReferenceId
          }
        ];
      }
      return currentSlots;
    },
    initialSlot ? [initialSlot] : []
  );
  return slots.filter(({ end, start }) => isSameDay(start, end)).reduce((group, date) => {
    const isGroupValid = isValid(date.start) && isValid(date.end) && !isEqual(date.start, date.end) && differenceInMinutes(date.end, date.start) >= SLOT_TIME;
    if (!isGroupValid)
      return group;
    const intervals = splitSlotsToIntervals(date, employeeReferenceId);
    return [...group, intervals];
  }, []);
};
const isNonWorkingSlot = ({ start: slotStart }, nonWorkingTimes) => nonWorkingTimes.some(({ start, end }) => isWithinInterval(slotStart, { start, end }));
const mergeNonWorkingSlots = (slots) => slots.sort(sortByStartTime).reduce((accumulator, slot) => {
  const previousSlot = accumulator[accumulator.length - 1];
  const isOverlapping = previousSlot && isBefore(previousSlot.start, previousSlot.end) && isWithinInterval(slot.start, {
    start: previousSlot.start,
    end: previousSlot.end
  });
  if (isOverlapping) {
    const minStart = min([previousSlot.start, slot.start]);
    const maxEnd = max([previousSlot.end, slot.end]);
    return [
      ...accumulator.slice(0, accumulator.length - 1),
      {
        ...slot,
        start: minStart,
        end: maxEnd
      }
    ];
  }
  return [...accumulator, slot];
}, []);
export {
  getFreeSlots,
  getSlotHeight,
  getSlotTopPosition,
  isNonWorkingSlot,
  isOverlappingWithNonWorkingTime,
  isPastAppointment,
  mergeNonWorkingSlots
};
